import { useState } from "react";

import { btn } from "@/components/Page";

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

export const MiningSmeltingPanel = ({ recipes, equipmentFragments, smeltBusyKey, onSmelt }) => {
  const [batchMap, setBatchMap] = useState({});
  const getBatches = (rarity) => Math.max(1, Number(batchMap[rarity] || 1));

  return (
    <section data-testid="mining-smelting-panel" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm space-y-4">
      <div>
        <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Smelting Recipes</h2>
        <p className="text-xs text-zinc-500 mt-1">Each recipe below converts raw ore into the regular fragments used by your blacksmith.</p>
      </div>

      <div className="grid xl:grid-cols-2 gap-4">
        {(recipes || []).map((recipe) => {
          const batches = getBatches(recipe.rarity);
          const busyWeapon = smeltBusyKey === `${recipe.rarity}-weapon`;
          const busyArmor = smeltBusyKey === `${recipe.rarity}-armor`;
          const totalOre = recipe.ore_cost * batches;
          const totalOutput = recipe.output_fragments * batches;
          const disabled = recipe.max_batches < batches;
          const exceedsRunLimit = batches > recipe.max_batches && recipe.batches_by_ore > recipe.max_batches;

          return (
            <article key={recipe.rarity} data-testid={`mining-smelt-card-${recipe.rarity}`} className="border border-zinc-800 bg-zinc-900/60 p-4 rounded-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-cinzel text-base text-zinc-100 capitalize">{recipe.rarity} raw ore</div>
                  <div data-testid={`mining-smelt-owned-${recipe.rarity}`} className="text-xs text-zinc-400 mt-2">
                    You currently have <span className="text-zinc-100">{pluralize(recipe.ore_have, `${recipe.rarity} ore`)}</span>.
                  </div>
                  <div data-testid={`mining-smelt-max-batches-${recipe.rarity}`} className="text-xs text-zinc-400 mt-1">
                    You have enough ore for <span className="text-zinc-100">{pluralize(recipe.batches_by_ore ?? recipe.max_batches, "smelt")}</span>.
                  </div>
                  {(recipe.batches_by_ore ?? 0) > recipe.max_batches && <div data-testid={`mining-smelt-batch-limit-${recipe.rarity}`} className="text-xs text-amber-300 mt-1">Each run is limited to {recipe.batch_limit || recipe.max_batches} smelts. Run again for more.</div>}
                </div>
                <div className="text-right text-xs text-zinc-400">
                  <div>Weapon fragments: <span data-testid={`mining-fragments-weapon-${recipe.rarity}`} className="text-zinc-100">{equipmentFragments?.weapon?.[recipe.rarity] ?? 0}</span></div>
                  <div className="mt-1">Armor fragments: <span data-testid={`mining-fragments-armor-${recipe.rarity}`} className="text-zinc-100">{equipmentFragments?.armor?.[recipe.rarity] ?? 0}</span></div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 text-xs text-zinc-400">
                <div data-testid={`mining-smelt-ore-cost-${recipe.rarity}`} className="border border-zinc-800 bg-zinc-950/50 p-3 rounded-sm">
                  Each smelt uses <span className="text-zinc-100">{pluralize(recipe.ore_cost, `${recipe.rarity} ore`)}</span>.
                </div>
                <div data-testid={`mining-smelt-output-${recipe.rarity}`} className="border border-zinc-800 bg-zinc-950/50 p-3 rounded-sm">
                  Each smelt creates <span className="text-zinc-100">{pluralize(recipe.output_fragments, `${recipe.rarity} fragment`)}</span>.
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-zinc-400">
                  <span className="block text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">How many smelts to run</span>
                  <input
                    data-testid={`mining-smelt-batches-${recipe.rarity}`}
                    type="number"
                    min={1}
                    max={Math.max(1, recipe.max_batches || 1)}
                    value={batchMap[recipe.rarity] || 1}
                    onChange={(event) => setBatchMap((prev) => ({ ...prev, [recipe.rarity]: event.target.value }))}
                    className="w-24 bg-zinc-950 border border-amber-900/40 px-3 py-2 text-zinc-100 rounded-sm outline-none focus:border-amber-600"
                  />
                </label>
                <button data-testid={`mining-smelt-set-max-${recipe.rarity}`} type="button" onClick={() => setBatchMap((prev) => ({ ...prev, [recipe.rarity]: recipe.max_batches }))} disabled={!recipe.max_batches} className={btn("ghost") + " text-xs"}>Smelt {recipe.max_batches || 0}</button>
                <div data-testid={`mining-smelt-total-${recipe.rarity}`} className="text-xs text-zinc-400">
                  This run will spend <span className="text-zinc-100">{pluralize(totalOre, `${recipe.rarity} ore`)}</span> and create <span className="text-zinc-100">{pluralize(totalOutput, `${recipe.rarity} fragment`)}</span>.
                </div>
              </div>

              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <button
                  data-testid={`mining-smelt-weapon-${recipe.rarity}`}
                  disabled={disabled || busyWeapon || busyArmor}
                  onClick={() => onSmelt(recipe.rarity, "weapon", batches)}
                  className={btn() + " justify-center"}
                >
                  {busyWeapon ? "Smelting" : "Create Weapon Fragments"}
                </button>
                <button
                  data-testid={`mining-smelt-armor-${recipe.rarity}`}
                  disabled={disabled || busyWeapon || busyArmor}
                  onClick={() => onSmelt(recipe.rarity, "armor", batches)}
                  className={btn("ghost") + " justify-center"}
                >
                  {busyArmor ? "Smelting" : "Create Armor Fragments"}
                </button>
              </div>

              {disabled && (
                <div data-testid={`mining-smelt-disabled-${recipe.rarity}`} className="text-xs uppercase tracking-[0.18em] text-amber-400">
                  {exceedsRunLimit ? `This run is limited to ${recipe.max_batches} smelts. Split larger smelts into multiple runs.` : "You do not have enough raw ore."}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};